import { skipToken } from "@reduxjs/toolkit/query";
import { useGetQuestionnaireSectionsQuery } from "../api/questionnaire";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { useNotarizationState } from "./useNotarizationState";
import {
  calculateCurrentStep,
  TOTAL_STEPS,
} from "../utils/progressTrackerUtils";
import { hasAllRequiredIncomeYears } from "../utils/incomeValidation";
import useCurrentUserPaymentData from "./useCurrentUserPaymentData";
import { useGetIncomesByUserIdQuery } from "../api/financials/incomes";

export interface UseProgressTrackerStateReturn {
  currentStep: number;
  totalSteps: number;
  percentage: number;
  label: string;
  isLoading: boolean;
  error: boolean;
}

export const useProgressTrackerState = (): UseProgressTrackerStateReturn => {
  const { user, document, error: documentError } = useGetCurrentUserDocument();
  const { isPrenupPaid } = useCurrentUserPaymentData();
  const {
    isDocumentReviewed,
    isNotarizationComplete,
    error: notarizationError,
  } = useNotarizationState();

  const { data: questionnaireSections, error: questionnaireError } =
    useGetQuestionnaireSectionsQuery(
      user?.id ? { id: user.id, includeStatus: true } : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { data: incomes } = useGetIncomesByUserIdQuery(
    user?.id && document?.id
      ? { userId: user.id, docId: document.id }
      : skipToken,
  );

  // Check for errors
  const hasError = Boolean(
    documentError ?? notarizationError ?? questionnaireError,
  );

  // Check if data is still loading
  const isLoading = !questionnaireSections && !hasError;

  const isFinancialsComplete = hasAllRequiredIncomeYears(
    incomes ?? [],
    document,
  );

  const currentStep = calculateCurrentStep({
    questionnaireSections: questionnaireSections ?? [],
    isPrenupPaid: isPrenupPaid ?? false,
    hasFinancials: isFinancialsComplete,
    isDocumentReviewed,
    isNotarizationComplete,
  });

  const percentage = Math.round((currentStep / TOTAL_STEPS) * 100);

  const label = percentage === 0 ? "10%" : `${percentage}%`;

  return {
    currentStep,
    totalSteps: TOTAL_STEPS,
    percentage,
    label,
    isLoading,
    error: hasError,
  };
};
